import { createReducer } from '@reduxjs/toolkit';
import { ManipulationState } from './types';
import {
  closeComponentIsolationMode,
  dragComponent,
  openComponentIsolationMode,
  setComponentIsolationEditedStyleClass,
  setComponentSelection,
  setCurrentView,
  setFocusedEditorArea,
  setHoveredComponent,
  activateRightSideTab,
  stopComponentDrag,
  setHighlightedSpacingDirection,
  setAdapterDataMappingOpen,
  setInitialComponentSelection,
} from './actions';

export const initialState: ManipulationState = {
  viewId: null,
  hoveredComponentId: null,
  componentDrag: null,
  selectedComponentId: null,
  initiallySelectedComponentId: null,
  isolation: null,
  focusedArea: 'ui',
  activeRightSideTab: 'properties',
  highlightedSpacingDirection: null,
  adapterDataMappingOpen: false,
};

const manipulationReducer = createReducer(initialState, (builder) => {
  /**
   * Define in which view the user is currently working on.
   */
  builder.addCase(setCurrentView, (state, action) => {
    const viewId = action.payload;
    if (viewId !== state.viewId) {
      state.viewId = viewId;
      // Clear any selection or isolation mode
      state.hoveredComponentId = null;
      state.selectedComponentId = null;
      state.isolation = null;
      state.adapterDataMappingOpen = false;
    }
  });
  /**
   * Hover on top of a component instance
   */
  builder.addCase(setHoveredComponent, (state, action) => {
    const hoveredComponentId = action.payload;
    state.hoveredComponentId = hoveredComponentId;
  });
  /**
   * Select a component instance
   */
  builder.addCase(setComponentSelection, (state, action) => {
    const selectedComponentId = action.payload;
    if (selectedComponentId == null) {
      state.selectedComponentId = state.isolation?.componentId ?? null;
    } else {
      state.selectedComponentId = selectedComponentId;
    }
    state.focusedArea = 'ui';
  });
  /**
   * Set initial component selection (before render / navigate)
   */
  builder.addCase(setInitialComponentSelection, (state, action) => {
    const initiallySelectedComponentId = action.payload;
    if (initiallySelectedComponentId != null) {
      state.initiallySelectedComponentId = initiallySelectedComponentId;
    }
  });
  /**
   * Drag a component.
   */
  builder.addCase(dragComponent, (state, action) => {
    const componentDrag = action.payload;
    state.componentDrag = componentDrag;
    state.focusedArea = 'ui';
  });
  /**
   * Stop component dragging
   */
  builder.addCase(stopComponentDrag, (state) => {
    state.componentDrag = null;
  });
  /**
   * Open the isolation mode
   */
  builder.addCase(openComponentIsolationMode, (state, action) => {
    const isolation = action.payload;
    state.isolation = isolation;
    state.selectedComponentId = isolation.componentId;
    state.focusedArea = 'ui';
  });
  /**
   * Close the isolation mode
   */
  builder.addCase(closeComponentIsolationMode, (state) => {
    // Leave the selection to the component that was isolated before the exit
    state.selectedComponentId = state.isolation?.componentId ?? null;
    state.isolation = null;
    state.focusedArea = 'ui';
  });
  /**
   * Change isolation mode edited style class.
   */
  builder.addCase(setComponentIsolationEditedStyleClass, (state, action) => {
    const editedStyleClass = action.payload;
    const { isolation } = state;
    if (isolation) {
      isolation.editedStyleClass = editedStyleClass;
    }
    state.focusedArea = 'ui';
  });
  /**
   * Change the focused editor area.
   */
  builder.addCase(setFocusedEditorArea, (state, action) => {
    const editorArea = action.payload;
    state.focusedArea = editorArea;
  });
  /**
   * Change the active right side tab.
   */
  builder.addCase(activateRightSideTab, (state, action) => {
    const rightSideTab = action.payload;
    state.activeRightSideTab = rightSideTab;
  });
  /**
   * Change the highlighted spacing direction.
   */
  builder.addCase(setHighlightedSpacingDirection, (state, action) => {
    const highlightedSpacingDirection = action.payload;
    state.highlightedSpacingDirection = highlightedSpacingDirection;
  });
  /**
   * Change the adapter data mapping dialog open state.
   */
  builder.addCase(setAdapterDataMappingOpen, (state, action) => {
    const adapterDataMappingOpen = action.payload;
    state.adapterDataMappingOpen = adapterDataMappingOpen;
  });
});

export default manipulationReducer;
